import { round } from 'lodash';

import { LoyaltyTransactionsBreakdown, RetailerNetworkPerformanceFragment } from '@occc/graphql';
import { formatCompactNumber } from '@occc-app/lib/numbers';

import { RetailerNetworkPerformanceDetails } from './retailer-network-performance.types';

type GetStorePerformance = (
  retailerNetworkPerformance: RetailerNetworkPerformanceFragment | undefined,
  field: keyof Pick<RetailerNetworkPerformanceFragment, 'newMembers' | 'loyaltyTransactions'>,
) => number;

type GetTransactionBreakdownPerformance = (
  retailerNetworkPerformance: RetailerNetworkPerformanceFragment | undefined,
  field: keyof Pick<LoyaltyTransactionsBreakdown, 'fuelAmount' | 'fuelVolume' | 'merchandise'>,
) => number;

const PERFORMANCE_ITEM_DEFAULT_VALUE = 0;

const getProgress: GetStorePerformance = (retailerNetworkPerformance, field) => {
  const value =
    retailerNetworkPerformance?.[field].goalProgress.progress || PERFORMANCE_ITEM_DEFAULT_VALUE;

  return round(value);
};

const getPerStoreAvg: GetStorePerformance = (retailerNetworkPerformance, field) => {
  const value =
    retailerNetworkPerformance?.[field].perStoreAverage || PERFORMANCE_ITEM_DEFAULT_VALUE;

  return round(value);
};

const getTotal: GetTransactionBreakdownPerformance = (retailerNetworkPerformance, field) => {
  const value =
    retailerNetworkPerformance?.loyaltyTransactionsBreakdown[field].total ||
    PERFORMANCE_ITEM_DEFAULT_VALUE;

  return round(value);
};

const getPerTransactionAvg: GetTransactionBreakdownPerformance = (
  retailerNetworkPerformance,
  field,
) => {
  const value =
    retailerNetworkPerformance?.loyaltyTransactionsBreakdown[field].perTransactionAverage ||
    PERFORMANCE_ITEM_DEFAULT_VALUE;

  return round(value);
};

export const parseRetailerNetworkPerformanceDetails = (
  retailerNetworkPerformance?: RetailerNetworkPerformanceFragment,
): RetailerNetworkPerformanceDetails => ({
  newMembersProgress: getProgress(retailerNetworkPerformance, 'newMembers'),
  newMembersPerStoreAvg: getPerStoreAvg(retailerNetworkPerformance, 'newMembers'),
  loyaltyTransactions: getProgress(retailerNetworkPerformance, 'loyaltyTransactions'),
  loyaltyTransactionsPerStoreAvg: getPerStoreAvg(retailerNetworkPerformance, 'loyaltyTransactions'),
  fuelAmount: getTotal(retailerNetworkPerformance, 'fuelAmount'),
  fuelAmountPerTransactionAvg: getPerTransactionAvg(retailerNetworkPerformance, 'fuelAmount'),
  fuelVolume: getTotal(retailerNetworkPerformance, 'fuelVolume'),
  fuelVolumePerTransactionAvg: getPerTransactionAvg(retailerNetworkPerformance, 'fuelVolume'),
  merchandise: getTotal(retailerNetworkPerformance, 'merchandise'),
  merchandisePerTransactionAvg: getPerTransactionAvg(retailerNetworkPerformance, 'merchandise'),
});

export const formatPerformanceNumber = (number: number) => {
  if (number < 1000) {
    return formatCompactNumber(number);
  }

  return formatCompactNumber(round(number, -2));
};

export const formatPerformanceAmount = (value: number): string =>
  `$${formatPerformanceNumber(value)}`;
